import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: 'Unsubscribe — Scott Ranch Sand & Gravel' },
      { name: 'description', content: 'Manage your email preferences for Scott Ranch Sand & Gravel.' },
      { property: 'og:title', content: 'Unsubscribe — Scott Ranch Sand & Gravel' },
      { property: 'og:description', content: 'Manage your email preferences for Scott Ranch Sand & Gravel.' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
})

type Status = 'loading' | 'ready' | 'already' | 'invalid' | 'submitting' | 'success' | 'error'

function UnsubscribePage() {
  const [status, setStatus] = useState<Status>('loading')
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const t = params.get('token')
    setToken(t)
    if (!t) {
      setStatus('invalid')
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) return setStatus('invalid')
        if (data.valid) return setStatus('ready')
        if (data.reason === 'already_unsubscribed') return setStatus('already')
        setStatus('invalid')
      })
      .catch(() => setStatus('error'))
  }, [])

  async function confirm() {
    if (!token) return
    setStatus('submitting')
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json().catch(() => ({}))
      if (data.success) setStatus('success')
      else if (data.reason === 'already_unsubscribed') setStatus('already')
      else setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-md border-2 border-ink bg-card p-8 text-center shadow-sm">
        <h1 className="text-2xl font-bold text-foreground">Email preferences</h1>
        <p className="mt-2 text-sm text-muted-foreground">Scott Ranch Sand &amp; Gravel</p>

        {status === 'loading' && (
          <p className="mt-6 text-sm text-muted-foreground">Checking your link…</p>
        )}
        {status === 'ready' && (
          <>
            <p className="mt-6 text-sm text-foreground">
              Click below to unsubscribe from Scott Ranch Sand &amp; Gravel emails.
            </p>
            <button
              onClick={confirm}
              className="mt-6 inline-flex w-full items-center justify-center rounded-md border-2 border-ink bg-ink px-4 py-3 text-sm font-semibold text-background transition hover:bg-accent hover:text-accent-foreground"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {status === 'submitting' && (
          <p className="mt-6 text-sm text-muted-foreground">Processing…</p>
        )}
        {status === 'success' && (
          <p className="mt-6 text-sm text-foreground">
            You've been unsubscribed. You won't receive further emails from us.
          </p>
        )}
        {status === 'already' && (
          <p className="mt-6 text-sm text-foreground">
            This email has already been unsubscribed.
          </p>
        )}
        {status === 'invalid' && (
          <p className="mt-6 text-sm text-destructive">
            This unsubscribe link is invalid or expired.
          </p>
        )}
        {status === 'error' && (
          <p className="mt-6 text-sm text-destructive">
            Something went wrong. Please try again later.
          </p>
        )}
      </div>
    </main>
  )
}
